import { useState } from "react";
import { ActivityIndicator, Pressable, StyleSheet, View } from "react-native";

import {
  AppText,
  Badge,
  Button,
  ButtonText,
  EmptyState,
  Stack,
  TextField,
} from "~/components/ui";
import { RANK_LABELS, useTaxonSearch } from "~/state/taxonomy";
import type { TaxonSearchResult } from "~/state/taxonomy";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

/**
 * The primary way plants get added: type anything — a botanical name, a
 * cultivar, or a nickname like "swiss cheese" — and pick a match. When nothing
 * matches, the caller offers the contribution flow instead.
 */
export function TaxonSearchField({
  onSelect,
  onRequestContribute,
  selected,
  onClear,
}: {
  onSelect: (taxon: TaxonSearchResult) => void;
  onRequestContribute?: (term: string) => void;
  selected?: TaxonSearchResult | null;
  onClear?: () => void;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const [term, setTerm] = useState("");
  const search = useTaxonSearch(term);
  const results = search.data ?? [];
  const searching = term.trim().length >= 2;

  if (selected) {
    return (
      <View style={stylesheet.selected}>
        <View style={stylesheet.selectedCopy}>
          <AppText weight="semibold">{selected.scientificName}</AppText>
          <AppText size="xs">
            {[
              selected.commonName,
              RANK_LABELS[selected.rank],
              selected.familyName,
            ]
              .filter(Boolean)
              .join(" · ")}
          </AppText>
        </View>
        {onClear ? (
          <Button variant="outline" compact onPress={onClear}>
            <ButtonText>Change</ButtonText>
          </Button>
        ) : null}
      </View>
    );
  }

  return (
    <Stack gap="sm">
      <TextField
        accessibilityLabel="Search plant names"
        value={term}
        placeholder="Monstera deliciosa, pohutukawa, tomato..."
        autoCapitalize="none"
        autoCorrect={false}
        onChangeText={setTerm}
      />

      {searching && search.isLoading ? (
        <View style={stylesheet.loading}>
          <ActivityIndicator />
        </View>
      ) : null}

      {searching && typeof navigator !== "undefined" && !navigator.onLine ? (
        <AppText size="xs" tone="danger">
          Search is unavailable offline. You can still add a plant by nickname.
        </AppText>
      ) : search.error ? (
        <AppText size="xs" tone="danger">
          Search is unavailable offline. You can still add a plant by nickname.
        </AppText>
      ) : null}

      {searching && !search.isLoading && results.length === 0 ? (
        <EmptyState
          title="No match in the botanical index"
          description="Add the botanical details yourself and they'll become part of the shared plant database."
        />
      ) : null}

      {results.length > 0 ? (
        <View style={stylesheet.results}>
          {results.map((taxon) => (
            <Pressable
              key={taxon.id}
              accessibilityRole="button"
              accessibilityLabel={`Select ${taxon.scientificName}`}
              onPress={() => onSelect(taxon)}
              style={({ hovered, pressed }) => [
                stylesheet.result,
                hovered || pressed ? stylesheet.resultHover : null,
              ]}
            >
              <View style={stylesheet.resultCopy}>
                <AppText weight="semibold">{taxon.scientificName}</AppText>
                <AppText size="xs">
                  {[taxon.commonName, RANK_LABELS[taxon.rank], taxon.familyName]
                    .filter(Boolean)
                    .join(" · ")}
                </AppText>
              </View>
              {taxon.verified ? (
                <Badge tone="success">Verified</Badge>
              ) : (
                <Badge tone="neutral">Community</Badge>
              )}
            </Pressable>
          ))}
        </View>
      ) : null}

      {onRequestContribute && term.trim().length >= 2 && !search.isLoading ? (
        <Button
          variant="outline"
          align="start"
          iconName="plus"
          onPress={() => onRequestContribute(term.trim())}
        >
          <ButtonText>Add "{term.trim()}" to the botanical index</ButtonText>
        </Button>
      ) : null}
    </Stack>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    loading: { paddingVertical: spacing.m },
    results: { gap: spacing.s },
    result: {
      minHeight: 56,
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.m,
      backgroundColor: colors.elevated,
    },
    resultHover: { backgroundColor: colors.surface },
    resultCopy: { flex: 1, minWidth: 0, gap: 2 },
    selected: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.m,
      padding: spacing.m,
      borderWidth: 1,
      borderColor: colors.primary,
      borderRadius: radii.m,
      backgroundColor: colors.successSurface,
    },
    selectedCopy: { flex: 1, minWidth: 0, gap: 2 },
  });
